import { useState } from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import type { WorkflowWrite } from '../../client'
import { apiErrorMessage } from '../../lib/api'
import Spinner from '../chat/Spinner'
import Modal from '../ui/Modal'

const INPUT =
  'w-full rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-base text-stone-900 placeholder:text-stone-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 focus:outline-none sm:text-sm'
const LABEL = 'text-xs font-medium text-stone-700'
const ICON_BUTTON =
  'flex size-10 shrink-0 items-center justify-center rounded-md text-stone-400 transition hover:bg-stone-100 hover:text-red-600 disabled:opacity-30 sm:size-8'
const ADD = 'flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900'
const NAME_PATTERN = /^[a-z][a-z0-9_]{0,39}$/
const MAX_STEPS = 10
const MAX_INPUTS = 10

function slug(label: string) {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, '')
  return base.slice(0, 40)
}

function problem(draft: WorkflowWrite) {
  if (!draft.name.trim()) return 'Give the workflow a name.'
  if (draft.steps.length === 0 || draft.steps.some((step) => !step.title.trim())) return 'Every step needs a title.'
  const names = (draft.inputs ?? []).map(({ name }) => name)
  if (names.some((name) => !NAME_PATTERN.test(name))) return 'Input names use lowercase letters, digits and _.'
  if (new Set(names).size !== names.length) return 'Each input needs its own name.'
  if ((draft.inputs ?? []).some(({ label }) => !label.trim())) return 'Every input needs a label.'
  const text = draft.steps.map(({ title, detail }) => `${title} ${detail ?? ''}`).join(' ')
  const unknown = [...text.matchAll(/\{([a-z][a-z0-9_]*)\}/g)].find(([, name]) => !names.includes(name))
  if (unknown) return `A step uses ${unknown[0]}, which isn't an input.`
  return null
}

type Props = {
  title: string
  initial: WorkflowWrite | null
  saveLabel: string
  onSave: (workflow: WorkflowWrite) => Promise<void>
  onClose: () => void
}

function Editor({ initial, saveLabel, onSave, onClose }: Omit<Props, 'title' | 'initial'> & { initial: WorkflowWrite }) {
  const [draft, setDraft] = useState<WorkflowWrite>({ ...initial, inputs: initial.inputs ?? [] })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputs = draft.inputs ?? []
  const invalid = problem(draft)

  function patch(change: Partial<WorkflowWrite>) {
    setDraft((current) => ({ ...current, ...change }))
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      await onSave({
        ...draft,
        name: draft.name.trim(),
        description: draft.description?.trim() ?? '',
        steps: draft.steps.map((step) => ({ ...step, title: step.title.trim(), detail: step.detail?.trim() ?? '' })),
      })
    } catch (err) {
      setError(apiErrorMessage(err))
      setSaving(false)
    }
  }

  return (
    <>
      <div className="space-y-5 px-6 py-5">
        <div className="space-y-1">
          <label htmlFor="workflow-name" className={LABEL}>
            Name
          </label>
          <input
            id="workflow-name"
            value={draft.name}
            maxLength={120}
            onChange={(event) => patch({ name: event.target.value })}
            className={INPUT}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="workflow-description" className={LABEL}>
            Description
          </label>
          <textarea
            id="workflow-description"
            value={draft.description ?? ''}
            rows={2}
            maxLength={1000}
            onChange={(event) => patch({ description: event.target.value })}
            className={`${INPUT} resize-none`}
          />
        </div>

        <section className="space-y-2">
          <div>
            <p className={LABEL}>Inputs</p>
            <p className="text-xs text-stone-500">
              Asked for on every run. Put <code className="text-purple-700">{'{name}'}</code> in a step to use one.
            </p>
          </div>
          {inputs.map((input, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                aria-label={`Input ${index + 1} label`}
                placeholder="Label, e.g. Product"
                value={input.label}
                onChange={(event) => {
                  const label = event.target.value
                  const auto = !input.name || input.name === slug(input.label)
                  patch({
                    inputs: inputs.map((item, at) =>
                      at === index ? { label, name: auto ? slug(label) : item.name } : item,
                    ),
                  })
                }}
                className={INPUT}
              />
              <div className="flex w-2/5 shrink-0 items-center rounded-lg border border-stone-300 bg-stone-50 pl-2 focus-within:border-purple-400">
                <span className="text-sm text-stone-400">{'{'}</span>
                <input
                  aria-label={`Input ${index + 1} name`}
                  value={input.name}
                  onChange={(event) =>
                    patch({
                      inputs: inputs.map((item, at) => (at === index ? { ...item, name: event.target.value } : item)),
                    })
                  }
                  className="w-full bg-transparent px-1 py-1.5 font-mono text-base text-stone-800 focus:outline-none sm:text-xs"
                />
                <span className="pr-2 text-sm text-stone-400">{'}'}</span>
              </div>
              <button
                type="button"
                aria-label={`Remove input ${index + 1}`}
                onClick={() => patch({ inputs: inputs.filter((_, at) => at !== index) })}
                className={ICON_BUTTON}
              >
                <TrashIcon className="size-4" />
              </button>
            </div>
          ))}
          {inputs.length < MAX_INPUTS && (
            <button
              type="button"
              onClick={() => patch({ inputs: [...inputs, { name: '', label: '' }] })}
              className={ADD}
            >
              <PlusIcon className="size-3.5" />
              Add input
            </button>
          )}
        </section>

        <section className="space-y-2">
          <p className={LABEL}>Steps</p>
          {draft.steps.map((step, index) => (
            <div key={index} className="flex gap-2">
              <span className="mt-2 w-4 shrink-0 text-center text-xs font-semibold text-stone-500">{index + 1}</span>
              <div className="min-w-0 flex-1 space-y-1">
                <input
                  aria-label={`Step ${index + 1} title`}
                  value={step.title}
                  maxLength={200}
                  onChange={(event) =>
                    patch({
                      steps: draft.steps.map((item, at) => (at === index ? { ...item, title: event.target.value } : item)),
                    })
                  }
                  className={INPUT}
                />
                <textarea
                  aria-label={`Step ${index + 1} detail`}
                  value={step.detail ?? ''}
                  rows={2}
                  maxLength={2000}
                  onChange={(event) =>
                    patch({
                      steps: draft.steps.map((item, at) => (at === index ? { ...item, detail: event.target.value } : item)),
                    })
                  }
                  className={`${INPUT} resize-none`}
                />
              </div>
              <button
                type="button"
                aria-label={`Remove step ${index + 1}`}
                disabled={draft.steps.length === 1}
                onClick={() => patch({ steps: draft.steps.filter((_, at) => at !== index) })}
                className={ICON_BUTTON}
              >
                <TrashIcon className="size-4" />
              </button>
            </div>
          ))}
          {draft.steps.length < MAX_STEPS && (
            <button
              type="button"
              onClick={() => patch({ steps: [...draft.steps, { title: '', detail: '', tools: [] }] })}
              className={ADD}
            >
              <PlusIcon className="size-3.5" />
              Add step
            </button>
          )}
        </section>
      </div>

      <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-stone-100 bg-white px-6 py-3">
        {(error || invalid) && <p className="mr-auto text-xs text-red-600">{error ?? invalid}</p>}
        <button
          type="button"
          onClick={onClose}
          className="h-10 rounded-lg px-3 text-sm text-stone-600 transition hover:bg-stone-100 sm:h-8"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={invalid !== null || saving}
          onClick={() => void save()}
          className="h-10 rounded-lg bg-purple-700 px-3 text-sm font-medium text-white transition enabled:hover:bg-purple-800 disabled:opacity-50 sm:h-8"
        >
          {saving ? 'Saving…' : saveLabel}
        </button>
      </div>
    </>
  )
}

export default function WorkflowEditor({ title, initial, ...rest }: Props) {
  return (
    <Modal title={title} width="max-w-2xl" padded={false} onClose={rest.onClose}>
      {initial ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Editor initial={initial} {...rest} />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 px-6 py-12 text-sm text-stone-500">
          <Spinner className="size-5 border-purple-200 border-t-purple-700" />
          Turning the plan into a workflow…
        </div>
      )}
    </Modal>
  )
}
